import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useProjectStore } from '../store/projectStore';
import SafeIcon from '../common/SafeIcon';
import { LOGOS } from '../assets/logos';
import MediaTest from '../components/Interview/MediaTest';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiMic, FiVideo, FiClock, FiCheck, FiX, FiArrowRight, FiAlertTriangle, FiPlay } = FiIcons;

const InterviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getProjectById, addProspect } = useProjectStore();
  
  const [project, setProject] = useState(null);
  const [step, setStep] = useState('welcome'); // welcome, info, mediaTest, interview, review, complete
  const [candidateInfo, setCandidateInfo] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    cv: null
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [videoError, setVideoError] = useState(null);

  const videoRef = useRef(null);
  const chunksRef = useRef([]);

  // Fetch project data
  useEffect(() => {
    const projectData = getProjectById(id);
    if (!projectData) {
      toast.error('Proyecto no encontrado');
      navigate('/');
      return;
    }
    setProject(projectData);
  }, [id, getProjectById, navigate]);

  // Clean up media streams when component unmounts
  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      responses.forEach(response => {
        if (response.response && response.response.startsWith('blob:')) {
          URL.revokeObjectURL(response.response);
        }
      });
    };
  }, [mediaStream, responses]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          facingMode: "user",
          frameRate: { ideal: 30 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      setMediaStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
      }
      
      setVideoError(null);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setVideoError('Error al acceder a la cámara: ' + err.message);
    }
  };

  const stopCamera = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    setStep('mediaTest');
  };

  const handleMediaTestComplete = () => {
    setStep('interview');
    startCamera();
    startQuestion();
  };

  const startQuestion = () => {
    setIsPreparing(true);
    setTimeLeft(project.questions[currentQuestionIndex]?.thinkingTime || project.thinkingTime || 30);
    
    // Thinking timer
    const thinkingTimer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(thinkingTimer);
          setIsPreparing(false);
          setTimeLeft(project.questions[currentQuestionIndex]?.responseTime || project.responseTime || 60);
          return project.questions[currentQuestionIndex]?.responseTime || project.responseTime || 60;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startRecording = () => {
    if (!mediaStream) {
      toast.error('No hay acceso a la cámara');
      return;
    }

    try {
      chunksRef.current = [];
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        try {
          if (chunksRef.current.length === 0) {
            throw new Error('No se capturaron datos de video');
          }

          const blob = new Blob(chunksRef.current, { type: 'video/webm' });
          
          if (!blob || blob.size === 0) {
            throw new Error('Blob de video inválido o vacío');
          }

          const url = URL.createObjectURL(blob);
          
          const newResponse = {
            questionIndex: currentQuestionIndex,
            response: url,
            duration: (project.questions[currentQuestionIndex]?.responseTime || project.responseTime || 60) - timeLeft,
            blob: blob
          };

          setResponses(prev => {
            // Revocar URLs anteriores para evitar memory leaks
            const existingResponse = prev.find(r => r.questionIndex === currentQuestionIndex);
            if (existingResponse && existingResponse.response && existingResponse.response.startsWith('blob:')) {
              URL.revokeObjectURL(existingResponse.response);
            }
            // Filtrar respuestas anteriores para la misma pregunta
            const filteredResponses = prev.filter(r => r.questionIndex !== currentQuestionIndex);
            return [...filteredResponses, newResponse];
          });

          setIsRecording(false);
          setVideoError(null);
          toast.success('Video grabado correctamente');
        } catch (error) {
          console.error('Error procesando video:', error);
          setVideoError('Error al procesar el video: ' + error.message);
          setIsRecording(false);
        }
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);

      // Response timer
      const responseTimer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(responseTimer);
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Error al iniciar la grabación');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < project.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      startQuestion();
    } else {
      setStep('review');
      stopCamera();
    }
  };

  const retakeVideo = () => {
    // Limpiar respuesta anterior
    const existingResponse = responses.find(r => r.questionIndex === currentQuestionIndex);
    if (existingResponse && existingResponse.response && existingResponse.response.startsWith('blob:')) {
      URL.revokeObjectURL(existingResponse.response);
    }
    setResponses(prev => prev.filter(r => r.questionIndex !== currentQuestionIndex));
    setVideoError(null);
    startQuestion();
  };

  const handleSubmit = async () => {
    try {
      const responseData = responses.map(r => ({
        question: project.questions[r.questionIndex].text,
        response: r.response,
        duration: r.duration
      }));

      await addProspect({
        projectId: project.id,
        name: candidateInfo.name,
        lastName: candidateInfo.lastName,
        email: candidateInfo.email,
        phone: candidateInfo.phone,
        cv: candidateInfo.cv ? candidateInfo.cv.name : null,
        responses: responseData
      });

      // Limpiar blobs antes de navegar
      responses.forEach(response => {
        if (response.response && response.response.startsWith('blob:')) {
          URL.revokeObjectURL(response.response);
        }
      });

      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting interview:', error);
      toast.error('Error al enviar la entrevista: ' + error.message);
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando entrevista...</p>
        </div>
      </div>
    );
  }

  // Welcome Step
  if (step === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-blue-800 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-blue-600 p-8 text-white text-center">
            <img src={LOGOS.WHITE_TRANSPARENT} alt="VIXEA" className="h-16 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2">{project.title}</h1>
            <p className="text-blue-100 text-lg">{project.company}</p>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">¡Bienvenido a tu entrevista!</h2>
              <p className="text-gray-600 text-lg">{project.welcomeMessage}</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <SafeIcon icon={FiVideo} className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Video Entrevista</h3>
                <p className="text-gray-600 text-sm">{project.questions.length} preguntas en video</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <SafeIcon icon={FiClock} className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Tiempo Estimado</h3>
                <p className="text-gray-600 text-sm">{project.questions.length * 2} minutos aprox.</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <SafeIcon icon={FiCheck} className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Requisitos</h3>
                <p className="text-gray-600 text-sm">Cámara y micrófono</p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
              <h4 className="font-semibold text-amber-800 mb-2">Instrucciones importantes:</h4>
              <ul className="text-amber-700 space-y-1 text-sm">
                <li>• Asegúrate de estar en un lugar tranquilo y bien iluminado</li>
                <li>• Tendrás {project.thinkingTime} segundos para preparar cada respuesta</li>
                <li>• Cada respuesta tiene un límite de {project.responseTime} segundos</li>
                {project.allowRetake && <li>• Podrás volver a grabar tus respuestas si lo necesitas</li>}
              </ul>
            </div>
            
            <div className="text-center">
              <button
                onClick={() => setStep('info')}
                className="px-8 py-4 bg-primary text-white rounded-xl hover:bg-blue-700 transition-colors text-lg font-semibold flex items-center space-x-2 mx-auto"
              >
                <span>Comenzar Entrevista</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Info Step
  if (step === 'info') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-blue-800 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8"
        >
          <div className="text-center mb-8">
            <img src={LOGOS.BLUE_TRANSPARENT} alt="VIXEA" className="h-12 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Información Personal</h1>
            <p className="text-gray-600">Completa tus datos para continuar</p>
          </div>
          
          <form onSubmit={handleInfoSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={candidateInfo.name}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido *
                </label>
                <input
                  type="text"
                  value={candidateInfo.lastName}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, lastName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Tu apellido"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={candidateInfo.email}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, email: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="tu@email.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                value={candidateInfo.phone}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, phone: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="+1234567890"
              />
            </div>
            
            {project.requireCV && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Curriculum Vitae (PDF) *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, cv: e.target.files[0] })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            )}
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setStep('welcome')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Atrás
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continuar
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  // Media Test Step
  if (step === 'mediaTest') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-blue-800 flex items-center justify-center p-6">
        <MediaTest onComplete={handleMediaTestComplete} />
      </div>
    );
  }

  // Interview Step
  if (step === 'interview') {
    const currentQuestion = project.questions[currentQuestionIndex];
    const currentResponse = responses.find(r => r.questionIndex === currentQuestionIndex);
    
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        {/* Header */}
        <div className="bg-black bg-opacity-50 p-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={LOGOS.WHITE_TRANSPARENT} alt="VIXEA" className="h-8" />
              <div>
                <h1 className="text-lg font-semibold">{project.title}</h1>
                <p className="text-gray-300 text-sm">{project.company}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-300">
                Pregunta {currentQuestionIndex + 1} de {project.questions.length}
              </p>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span className="text-lg font-mono">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto p-6">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Video Panel */}
            <div className="space-y-4">
              <div className="bg-black rounded-xl aspect-video relative overflow-hidden">
                {isPreparing ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-600">
                    <div className="text-center">
                      <SafeIcon icon={FiClock} className="w-16 h-16 mx-auto mb-4 animate-pulse" />
                      <h3 className="text-2xl font-bold mb-2">Prepara tu respuesta</h3>
                      <p className="text-blue-100">Tiempo restante: {timeLeft}s</p>
                    </div>
                  </div>
                ) : isRecording ? (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                      style={{ transform: 'scaleX(-1)' }}
                    />
                    <div className="absolute top-4 left-4 flex items-center space-x-2 bg-red-600 px-3 py-1 rounded-full">
                      <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                      <span className="text-white text-sm font-medium">REC</span>
                    </div>
                    <div className="absolute top-4 right-4 bg-black bg-opacity-50 px-3 py-1 rounded-full">
                      <span className="text-white text-sm font-mono">
                        {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                  </>
                ) : currentResponse ? (
                  <div className="relative w-full h-full">
                    <video
                      src={currentResponse.response}
                      controls
                      className="w-full h-full object-cover"
                      playsInline
                      onLoadedData={() => {
                        console.log('Video preview loaded successfully');
                      }}
                      onError={(e) => {
                        console.error('Error loading video preview:', e);
                        setVideoError('Error al cargar la vista previa del video');
                      }}
                    />
                    <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 px-3 py-1 rounded-full">
                      <span className="text-white text-sm">Vista previa</span>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="text-center">
                      <SafeIcon icon={FiVideo} className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-300">Presiona Grabar cuando estés listo</p>
                    </div>
                  </div>
                )}

                {videoError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-red-900 bg-opacity-90">
                    <div className="text-center p-6">
                      <SafeIcon icon={FiAlertTriangle} className="w-12 h-12 mx-auto mb-4 text-red-400" />
                      <h3 className="text-lg font-semibold mb-2">Error de video</h3>
                      <p className="text-red-200 mb-4">{videoError}</p>
                      <button
                        onClick={retakeVideo}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Intentar de nuevo
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex justify-center space-x-4">
                {isPreparing ? (
                  <button
                    onClick={() => {
                      setIsPreparing(false);
                      setTimeLeft(project.questions[currentQuestionIndex]?.responseTime || project.responseTime || 60);
                      startRecording();
                    }}
                    className="px-8 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiVideo} className="w-5 h-5" />
                    <span>Comenzar a Grabar</span>
                  </button>
                ) : isRecording ? (
                  <button
                    onClick={stopRecording}
                    className="px-8 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                    <span>Detener</span>
                  </button>
                ) : currentResponse ? (
                  <div className="flex space-x-4">
                    {project.allowRetake && (
                      <button
                        onClick={retakeVideo}
                        className="px-6 py-3 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition-colors"
                      >
                        Grabar de nuevo
                      </button>
                    )}
                    <button
                      onClick={nextQuestion}
                      className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center space-x-2"
                    >
                      <span>{currentQuestionIndex < project.questions.length - 1 ? 'Siguiente' : 'Finalizar'}</span>
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setTimeLeft(project.questions[currentQuestionIndex]?.responseTime || project.responseTime || 60);
                      startRecording();
                    }}
                    className="px-8 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiVideo} className="w-5 h-5" />
                    <span>Grabar Respuesta</span>
                  </button>
                )}
              </div>
            </div>

            {/* Question Panel */}
            <div className="space-y-6">
              <div className="bg-white text-gray-900 rounded-xl p-6">
                <h2 className="text-2xl font-bold mb-4">{currentQuestion.text}</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <SafeIcon icon={FiClock} className="w-5 h-5" />
                    <span>
                      {isPreparing 
                        ? `Tiempo para preparar: ${timeLeft}s`
                        : `Tiempo para responder: ${timeLeft}s`
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-gray-600">
                    <SafeIcon icon={FiVideo} className="w-5 h-5" />
                    <span>Respuesta en video</span>
                  </div>
                </div>

                {isPreparing && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">💡 Consejos para una buena respuesta:</h3>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Mantén contacto visual con la cámara</li>
                      <li>• Habla de forma clara y pausada</li>
                      <li>• Estructura tu respuesta: introducción, desarrollo, conclusión</li>
                      <li>• Usa ejemplos específicos cuando sea posible</li>
                    </ul>
                  </div>
                )}

                {currentResponse && (
                  <div className="mt-6 p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-800">
                      <SafeIcon icon={FiCheck} className="w-5 h-5" />
                      <span className="font-semibold">Respuesta grabada correctamente</span>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      Duración: {Math.floor(currentResponse.duration / 60)}:{(currentResponse.duration % 60).toString().padStart(2, '0')}
                    </p>
                  </div>
                )}
              </div>

              {/* Progress */}
              <div className="bg-white bg-opacity-10 rounded-xl p-6">
                <h3 className="font-semibold mb-4">Progreso de la entrevista</h3>
                <div className="space-y-2">
                  {project.questions.map((_, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index < currentQuestionIndex ? 'bg-green-500 text-white' :
                        index === currentQuestionIndex ? 'bg-blue-500 text-white' :
                        'bg-gray-600 text-gray-300'
                      }`}>
                        {index < currentQuestionIndex ? <SafeIcon icon={FiCheck} className="w-4 h-4" /> : index + 1}
                      </div>
                      <span className={`${
                        index === currentQuestionIndex ? 'text-white font-semibold' : 'text-gray-300'
                      }`}>
                        Pregunta {index + 1}
                        {responses.find(r => r.questionIndex === index) && 
                          index !== currentQuestionIndex && 
                          <span className="ml-2 text-green-400">✓</span>
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review Step
  if (step === 'review') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-blue-800 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-8"
        >
          <div className="text-center mb-8">
            <img src={LOGOS.BLUE_TRANSPARENT} alt="VIXEA" className="h-12 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Revisa tus respuestas</h1>
            <p className="text-gray-600">Verifica que todo esté correcto antes de enviar</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {responses.map((response, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Pregunta {response.questionIndex + 1}: {project.questions[response.questionIndex].text}
                </h3>
                <div className="bg-black rounded-lg aspect-video relative overflow-hidden">
                  <video
                    src={response.response}
                    controls
                    className="w-full h-full object-cover"
                    playsInline
                  />
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 px-2 py-1 rounded text-white text-xs">
                    {Math.floor(response.duration / 60)}:{(response.duration % 60).toString().padStart(2, '0')}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h4 className="font-semibold text-blue-900 mb-2">Antes de enviar:</h4>
            <ul className="text-blue-800 space-y-1 text-sm">
              <li>• Revisa que todas tus respuestas sean claras y completas</li>
              <li>• Verifica que el audio se escuche correctamente</li>
              <li>• Una vez enviado, no podrás modificar tus respuestas</li>
            </ul>
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={() => {
                setStep('interview');
                setCurrentQuestionIndex(0);
                startCamera();
                startQuestion();
              }}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Editar respuestas
            </button>
            <button
              onClick={handleSubmit}
              className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <span>Enviar entrevista</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default InterviewPage;